import { Grid, LoadingOverlay, Modal, Space } from '@mantine/core';
import { useEffect } from 'react';
import { SmallModal } from '../../../components/SmallModal';
import { Title } from '../../../components/Title';
import { FormButtons } from '../../../components/form/FormButtons';
import { useModal } from '../../../hooks/useModal';
import { mutate } from '../../../util/api/mutate';
import type { OrganisationDto } from '../../../util/types/OrganisationDto';
import {
	type EditOrganisationDto,
	useEditOrganisationForm,
} from '../hooks/useEditOrganisationForm';
import { useServerOrganisationValidation } from '../hooks/useServerOrganisationValidation';
import { OrganisationInputs } from './NewOrganisationModal';

interface OrganisationEditorModal {
	close: () => void;
	opened: boolean;
	triggerListUpdate: () => void;
	organisation: EditOrganisationDto;
}

export function EditOrganisationModal({
	opened,
	close,
	triggerListUpdate,
	organisation,
}: OrganisationEditorModal) {
	const successModal = useModal();

	const { form, inputs } = useEditOrganisationForm(organisation);

	const newOrganisationMutation = mutate<EditOrganisationDto, OrganisationDto>(
		'organisation/edit',
		inputs,
	);

	useServerOrganisationValidation(newOrganisationMutation, form);

	useEffect(() => {
		form.setValues(organisation);
	}, [organisation]);

	const resetAndClose = () => {
		form.reset();
		triggerListUpdate();
		close();
		successModal.close();
	};

	return (
		<>
			<Modal
				opened={opened}
				onClose={close}
				title={<Title label={'Edit Organisation'} />}
				w={100}
				withCloseButton={true}
				size={'lg'}
				centered
			>
				<LoadingOverlay visible={newOrganisationMutation.isLoading} />
				<SmallModal
					close={resetAndClose}
					isOpen={successModal.isOpen}
					title={'Organisation Edited!'}
					body={
						'An organisation has been successfully updated. Click Ok to continue...'
					}
				/>

				<Space h={6} />
				<form
					onSubmit={form.onSubmit(async () => {
						await newOrganisationMutation.mutateAsync().then((value) => {
							if (value.isValid) {
								successModal.open();
							}
						});
					})}
				>
					<Grid>
						<OrganisationInputs form={form} />
					</Grid>
					<FormButtons close={close} />
				</form>
			</Modal>
		</>
	);
}
